import classNames from 'classnames';
import type { ComponentProps, HTMLAttributes } from 'react';
import React, { forwardRef, useState } from 'react';
import { Corner } from '../../app/extension-ui/shared/types';
import { IconButton } from '../../components/Button';
import ModalPrimitive from '../../components/ModalPrimitive';
import ScrollArea from '../../components/ScrollArea';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillComponent from '../../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import type QuillEditor from '../../editor/QuillEditor';
import useAnchorScrollingHandler from '../../editor/useAnchorScrollingHandler';
import useHeaderCollapse from '../../editor/useHeaderCollapse';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import clearIcon from '../../icons/non-prefilled/16x16/clear.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import { LinkForceNewTabContext } from '../../utils/router/Link';
import * as styles from './PostModalView.module.scss';
import PostModalViewMoveable from './PostModalViewMoveable';
import PostModalViewResizeButton from './PostModalViewResizeButton';
import PostModalViewTOC from './PostModalViewTOC';

const POST_FRAGMENT = gql(/** @complexity 23 */ `
  fragment PostModalView_post on Post {
    id
    content {
      id
      delta
    }
    banner {
      original
    }
    ...PostModalViewTOC_post
  }
`);

const ENTITY_ASSET_TOKEN_FRAGMENT = gql(/** @complexity 1 */ `
  fragment PostModalView_entityAssetToken on EntityAssetToken {
    ...AssetTokenProvider_entityAssetToken
  }
`);

interface AnchorScrollingHandlerContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  postId: string;
  quill: QuillEditor | null;
}

const AnchorScrollingHandlerContainer = forwardRef<
  HTMLDivElement,
  AnchorScrollingHandlerContainerProps
>(({ children, postId, quill, ...props }, ref) => {
  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId);

  return (
    <div ref={ref} {...anchorScrollingHandlerProps} {...props}>
      {children}
    </div>
  );
});

interface InnerPostModalViewProps
  extends Omit<ComponentProps<typeof PostModalViewResizeButton>, 'corner'>,
    ComponentProps<typeof PostModalViewMoveable> {
  post?: DocumentType<typeof POST_FRAGMENT>;
  entityAssetToken?: DocumentType<typeof ENTITY_ASSET_TOKEN_FRAGMENT>;
  headerBar: React.ReactNode;
  editorFooter?: React.ReactNode;
  magneticWall?: React.ReactNode;
  newTabTo?: string;
  frameless?: boolean;
  onClose: () => void;
  onQuillMount?: (quill: QuillEditor) => void;
}

const InnerPostModalView = withQuillContextProvider(
  ({
    post,
    entityAssetToken,
    headerBar,
    editorFooter,
    magneticWall,
    newTabTo,
    frameless,
    onClose,
    onDraggingStart,
    onDraggingMove,
    onDraggingEnd,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
    onQuillMount,
  }: InnerPostModalViewProps) => {
    const { quill } = useQuill();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
      null,
    );
    useHeaderCollapse(quill);

    const moveableProps = onDraggingStart
      ? { onDraggingStart, onDraggingMove, onDraggingEnd }
      : {};

    const content = post && entityAssetToken && (
      <LinkForceNewTabContext.Provider value>
        <AnchorScrollingHandlerContainer
          postId={post.id}
          quill={quill}
          className={styles.container}
        >
          <ScrollArea className={styles.scrollArea}>
            <PostModalViewMoveable
              ref={setPortalContainer}
              className={classNames(styles.contentContainer, {
                [styles.isMoveable]: !!onDraggingStart,
              })}
              {...moveableProps}
            >
              {post.banner.original ? (
                <div className={styles.bannerContainer}>
                  <img
                    className={styles.banner}
                    draggable={false}
                    src={post.banner.original}
                    alt=""
                  />
                </div>
              ) : null}
              <div className={styles.tocContainer}>
                <PostModalViewTOC
                  post={post}
                  portalContainer={portalContainer}
                />
              </div>
              <div className={styles.headerBarContainer}>{headerBar}</div>
              <div className={styles.content}>
                <div className={styles.editorContainer}>
                  <AssetTokenProvider entityAssetToken={entityAssetToken}>
                    <QuillComponent
                      initialContent={post.content.delta}
                      rootClassName={styles.editorRoot}
                      contentPlaceholder=""
                      namespace="extensionPostCard"
                      readOnly
                      autoHeight
                      titlePlaceholder="Untitled"
                      onMount={onQuillMount}
                    />
                  </AssetTokenProvider>
                  {editorFooter}
                </div>
              </div>
              <div className={styles.padding} />
              <div className={styles.magneticWallContainer}>{magneticWall}</div>
            </PostModalViewMoveable>
          </ScrollArea>
          {Object.values(Corner).map((corner) => (
            <PostModalViewResizeButton
              key={corner}
              corner={corner}
              onResizeStart={onResizeStart}
              onResizeMove={onResizeMove}
              onResizeEnd={onResizeEnd}
            />
          ))}
          <div className={styles.buttonGroup}>
            {!!newTabTo && (
              <IconButton
                size="xs"
                to={newTabTo}
                className={styles.button}
                aria-label="Open in new tab"
              >
                {newTabIcon}
              </IconButton>
            )}
            <IconButton
              size="xs"
              className={styles.button}
              onClick={() => onClose()}
              aria-label="Close"
            >
              {clearIcon}
            </IconButton>
          </div>
        </AnchorScrollingHandlerContainer>
      </LinkForceNewTabContext.Provider>
    );

    return frameless ? (
      content
    ) : (
      <ModalPrimitive.Root aria-label={post?.title} className={styles.modal}>
        {content}
      </ModalPrimitive.Root>
    );
  },
);

type PostModalViewProps = InnerPostModalViewProps;

const PostModalView = (props: PostModalViewProps) => (
  <InnerPostModalView
    // Force component remount when post changes since Quill only reads `initialContent` on mount
    key={props.post?.id}
    {...props}
  />
);

export default PostModalView;
